'use client';

import { InsuranceProvider } from '@coolblue-development/next-insurance/insurance-selector';
import { ShoppingCartItem, ShoppingCartSubItem } from '@coolblue-development/next-shopping-cart';
import { MainProductShoppingCartItem } from '@features/add-to-cart-page/api/AddToCartPageRepositoryInterface';
import { ProductSummary } from '@features/add-to-cart-page/typings/Product';
import { createContext, useContext, useState } from 'react';

import { SummaryItem, SummaryItemPaymentType, SummaryItemType } from './SummaryStore.types';

type SummaryContextApi = {
  items: SummaryItem[];
  addItem(item: SummaryItem): void;
  removeItem(id: SummaryItem['id']): void;
  mainProductQuantity: number;
  mainProductShoppingCartItem?: MainProductShoppingCartItem;
  trackingSubFeature?: string;
};

const SummaryContext = createContext<SummaryContextApi | null>(null);

export function SummaryStoreProvider({
  children,
  legacyShoppingCartItems = [],
  product,
  mainProductId,
  mainProductShoppingCartItem,
  trackingSubFeature
}: {
  children: React.ReactNode;
  // TODO: This should be replaced with MandyAPI
  legacyShoppingCartItems?: ShoppingCartItem[];
  product?: ProductSummary;
  mainProductId: string;
  mainProductShoppingCartItem?: MainProductShoppingCartItem;
  trackingSubFeature?: string;
}) {
  const [items, setItems] = useState<SummaryItem[]>([
    ...getShoppingCartFreeItems(),
    ...getPromotions(),
    ...getInsurances(),
    ...getInstallationServices()
  ]);

  const mainProductQuantity = Number(
    legacyShoppingCartItems.find(item => item.productId === mainProductId)?.quantity
  );

  function getNumberValueForPrice(price: string) {
    if (isNaN(+price)) {
      return +price.replace('€', '').replace(',', '.');
    }
    return +price;
  }

  function getShoppingCartItem(): ShoppingCartItem | undefined {
    if (!product || legacyShoppingCartItems.length === 0) return;

    // We need to look for the main item from the end
    // because it's already sorted in ascending order from the source.
    return legacyShoppingCartItems.findLast(item => Number(item.productId) === product.id);
  }

  function getShoppingCartFreeItems() {
    const shoppingCartItem = getShoppingCartItem();

    if (!shoppingCartItem || !shoppingCartItem.freeWith || !product) {
      return [];
    }

    return shoppingCartItem.freeWith.freeWithProducts.map(item => ({
      id: item.productId,
      name: item.productFullName,
      quantity: 1,
      type: SummaryItemType.FreeWith,
      paymentType: SummaryItemPaymentType.OneTime,
      showPrice: true,
      salesPrice: {
        includingVat: 0
      }
    }));
  }

  function getItemPriceExcludingVat(subItemId: string) {
    const priceExcludingVat = Number(
      legacyShoppingCartItems.find(item => item.productId === subItemId)?.salesPriceExcludingVat
    );

    return priceExcludingVat ?? 0;
  }

  function mapCommonSummaryItemFields(item: ShoppingCartSubItem) {
    return {
      id: item.productId,
      name: item.productName,
      quantity: item.quantity,
      paymentType: SummaryItemPaymentType.OneTime
    };
  }

  function getPromotions() {
    const shoppingCartItem = getShoppingCartItem();

    if (!shoppingCartItem || !shoppingCartItem.subItems || !product) {
      return [];
    }

    const promotionProductIds: string[] = [];

    product.productPlusPromotions?.forEach(promotion =>
      promotion.products.map(product => promotionProductIds.push(product.id.toString()))
    );

    return shoppingCartItem.subItems.reduce<SummaryItem[]>((list, item) => {
      const isPromotion = promotionProductIds.includes(item.productId);
      const salesPriceExcludingVat = getItemPriceExcludingVat(item.productId);

      if (isPromotion) {
        list.push({
          ...mapCommonSummaryItemFields(item),
          showPrice: item.showPrice,
          salesPrice: {
            includingVat: item.isFree ? 0 : getNumberValueForPrice(item.salesPrice),
            excludingVat: salesPriceExcludingVat
          },
          promotion: product.productPlusPromotions?.find(promotion =>
            promotion.products.map(product => product.id.toString()).includes(item.productId)
          ),
          type: SummaryItemType.Promotion
        });
      }

      return list;
    }, []);
  }

  function getInsurances() {
    const shoppingCartItem = getShoppingCartItem();

    if (!shoppingCartItem || !shoppingCartItem.subItems || !product) {
      return [];
    }

    return shoppingCartItem.subItems.reduce<SummaryItem[]>((list, item) => {
      const hasInsurance = mainProductShoppingCartItem?.insurance?.productId === item.productId;
      const salesPriceExcludingVat = getItemPriceExcludingVat(item.productId);

      if (hasInsurance) {
        list.push({
          id:
            mainProductShoppingCartItem?.insurance?.provider === InsuranceProvider.CoverGenius
              ? mainProductShoppingCartItem?.insurance?.quoteId
              : item.productId,
          name: item.productName,
          quantity: item.quantity,
          paymentType: SummaryItemPaymentType.OneTime,
          showPrice: getNumberValueForPrice(item.salesPrice) > 0,
          salesPrice: {
            includingVat: getNumberValueForPrice(item.salesPrice),
            excludingVat: salesPriceExcludingVat
          },
          type: SummaryItemType.Insurance
        });
      }

      return list;
    }, []);
  }

  function getInstallationServices() {
    const shoppingCartItem = getShoppingCartItem();

    if (
      !shoppingCartItem ||
      !shoppingCartItem.subItems ||
      !product ||
      mainProductShoppingCartItem?.installationServices?.length === 0
    ) {
      return [];
    }

    return shoppingCartItem.subItems.reduce<SummaryItem[]>((list, item) => {
      const isInstallationService = (
        mainProductShoppingCartItem?.installationServices ?? []
      ).includes(item.productId);
      const salesPriceExcludingVat = getItemPriceExcludingVat(item.productId);

      if (isInstallationService) {
        list.push({
          ...mapCommonSummaryItemFields(item),
          showPrice: true,
          salesPrice: {
            includingVat: getNumberValueForPrice(item.salesPrice),
            excludingVat: salesPriceExcludingVat
          },
          type: SummaryItemType.Service
        });
      }

      return list;
    }, []);
  }

  function addItem(item: SummaryItem) {
    setItems(state => {
      const existingItem = state.find(i => i.id === item.id);

      if (existingItem) {
        return state.map(i =>
          i.id === existingItem.id ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }

      return state.concat({ ...item, quantity: item.quantity || 1 });
    });
  }

  function removeItem(id: SummaryItem['id']) {
    setItems(state => state.filter(i => i.id !== id));
  }

  return (
    <SummaryContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        mainProductQuantity,
        mainProductShoppingCartItem,
        trackingSubFeature
      }}
    >
      {children}
    </SummaryContext.Provider>
  );
}

export function useSummaryStore() {
  const context = useContext(SummaryContext);

  if (!context) {
    throw new Error('useSummaryStore must be used within a SummaryStoreProvider');
  }

  return context;
}
